"use client";
import {
  AbsoluteFill,
  Sequence,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

// ════════════════════════════════════════════════════════════════════
// MONEY FULL PREVIEW — transparent, word-stamp synced.
// 16fps · 1280x704 · base video 664.612s.
// Palette: deep green primary + parchment secondary + gold accent.
// production_id: 1bbff9e0-7ccb-46d7-a5ed-c2c0fbe4af13
// ════════════════════════════════════════════════════════════════════

const FPS = 16;
const sToF = (s: number) => Math.round(s * FPS);
const FONT = "Inter, system-ui, sans-serif";
const TS = "0 3px 14px rgba(0,0,0,0.92), 0 1px 5px rgba(0,0,0,0.95), 0 0 2px rgba(0,0,0,0.9)";

// Palette from spec
const PRIMARY    = "#1B4332"; // deep green
const SECONDARY  = "#e8dcc4"; // parchment
const TERTIARY   = "#a89968"; // muted gold
const ACCENT_GOLD = "#d4af37"; // bright gold
const DIM        = "#8a8270";
const TEXT       = "#f5f0e2";
const WHITE      = "#ffffff";

const appear = (t: number, atS: number, dur = 0.18) =>
  interpolate(t, [atS, atS + dur], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

const win = (t: number, inS: number, outS: number) =>
  interpolate(t, [inS, inS + 0.2, outS - 0.4, outS], [0, 1, 1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

// ─── 1 · STAMP — rotated framed callout ───────────────────────────────
const Stamp: React.FC<{ s: number; title: string; sub?: string; atS: number; outS: number; color?: string; rotate?: number }> = ({ s, title, sub, atS, outS, color = PRIMARY, rotate = -8 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const sc = spring({ frame: Math.max(0, frame - sToF(atS - s)), fps, config: { damping: 9, stiffness: 130 } });
  const rot = interpolate(t, [atS, atS + 0.35], [rotate - 6, rotate], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ transform: `rotate(${rot}deg) scale(${sc})`, border: `4px solid ${color}`, padding: "22px 48px", color, boxShadow: `0 0 28px ${color}55, 0 8px 26px rgba(0,0,0,0.7)`, background: "rgba(0,0,0,0.45)" }}>
        <div style={{ fontSize: 50, fontWeight: 900, letterSpacing: "0.07em", textShadow: TS }}>{title}</div>
        {sub && <div style={{ fontSize: 18, marginTop: 7, letterSpacing: "0.2em", textAlign: "center", textShadow: TS, color: TEXT }}>{sub}</div>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 2 · TEXT HIGHLIGHT — keyword punches in on its word ──────────────
const TextHighlight: React.FC<{ s: number; pre: string; hi: string; post: string; hiS: number; outS: number; hiColor?: string }> = ({ s, pre, hi, post, hiS, outS, hiColor = ACCENT_GOLD }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const preS = hiS - 0.3;
  const op = win(t, preS, outS);
  const hiOp = appear(t, hiS, 0.14);
  const hiSc = interpolate(t, [hiS, hiS + 0.25], [0.78, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ textAlign: "center", maxWidth: 1100 }}>
        {pre && <span style={{ color: DIM, fontSize: 36, fontWeight: 500, textShadow: TS, opacity: appear(t, preS, 0.2) }}>{pre} </span>}
        <span style={{ color: hiColor, fontSize: 64, fontWeight: 900, letterSpacing: "0.04em", textShadow: `0 0 22px ${hiColor}77, ${TS}`, opacity: hiOp, display: "inline-block", transform: `scale(${hiSc})` }}>{hi}</span>
        {post && <span style={{ color: DIM, fontSize: 36, fontWeight: 500, textShadow: TS, opacity: appear(t, hiS + 0.25, 0.2) }}> {post}</span>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 3 · LOWER THIRD — bottom-left, with accent bar ───────────────────
const LowerThird: React.FC<{
  s: number; name: string; sub: string; italic?: string; nameS: number; subS: number; outS: number;
}> = ({ s, name, sub, italic, nameS, subS, outS }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, nameS - 0.2, outS);
  const slideX = interpolate(t, [nameS - 0.25, nameS + 0.15], [-260, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: (v) => 1 - Math.pow(1 - v, 3),
  });
  const nameOp = appear(t, nameS, 0.16);
  const subOp = appear(t, subS, 0.2);
  const accentW = interpolate(t, [nameS - 0.2, nameS + 0.35], [0, 168], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ opacity: op }}>
      <div style={{ position: "absolute", bottom: 86, left: 64, transform: `translateX(${slideX}px)`, fontFamily: FONT }}>
        <div style={{ height: 3, width: accentW, background: ACCENT_GOLD, marginBottom: 10, boxShadow: `0 0 10px ${ACCENT_GOLD}99` }} />
        <div style={{ color: WHITE, fontSize: 34, fontWeight: 800, letterSpacing: "-0.4px", textShadow: TS, lineHeight: 1.05, opacity: nameOp }}>{name}</div>
        <div style={{ color: ACCENT_GOLD, fontSize: 16, fontWeight: 700, letterSpacing: "0.18em", marginTop: 6, textShadow: TS, opacity: subOp }}>{sub}</div>
        {italic && <div style={{ color: TERTIARY, fontSize: 13, fontStyle: "italic", marginTop: 4, textShadow: TS, opacity: subOp }}>{italic}</div>}
      </div>
    </AbsoluteFill>
  );
};

// ─── 4 · BIG STAT — number counts up and locks on the value word ──────
const BigStat: React.FC<{
  s: number; label: string; value: number; valueS: number; outS: number;
  decimals?: number; prefix?: string; suffix?: string; sub?: string;
}> = ({ s, label, value, valueS, outS, decimals = 0, prefix, suffix, sub }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const labelS = valueS - 0.25;
  const op = win(t, labelS, outS);
  const cur = interpolate(t, [valueS, valueS + 0.42], [0, value], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const numOp = appear(t, valueS, 0.12);
  const sc = spring({ frame: Math.max(0, frame - sToF(valueS - s)), fps, config: { damping: 13, stiffness: 95 } });
  const subOp = appear(t, valueS + 0.5, 0.3);
  const shown = value >= 100 ? Math.round(cur).toLocaleString() : cur.toFixed(decimals);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: ACCENT_GOLD, fontSize: 25, letterSpacing: "0.24em", marginBottom: 14, textShadow: TS, opacity: appear(t, labelS, 0.2), fontWeight: 700 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, opacity: numOp, transform: `scale(${sc})` }}>
        {prefix && <span style={{ color: TEXT, fontSize: 52, fontWeight: 800, textShadow: TS }}>{prefix}</span>}
        <span style={{ color: TEXT, fontSize: 168, fontWeight: 800, letterSpacing: "-4px", textShadow: TS, lineHeight: 1 }}>{shown}</span>
        {suffix && <span style={{ color: ACCENT_GOLD, fontSize: 56, fontWeight: 800, textShadow: TS }}>{suffix}</span>}
      </div>
      {sub && <div style={{ color: SECONDARY, fontSize: 21, letterSpacing: "0.16em", marginTop: 20, textShadow: TS, opacity: subOp }}>{sub}</div>}
    </AbsoluteFill>
  );
};

// ─── 5 · CHAPTER — PHASE 01..N / TITLE ────────────────────────────────
const Chapter: React.FC<{ s: number; num: string; title: string; sub?: string; numS: number; titleS: number; outS: number; labelText?: string }> = ({ s, num, title, sub, numS, titleS, outS, labelText = "PHASE" }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const t = s + frame / FPS;
  const labelS = numS - 0.3;
  const op = win(t, labelS, outS);
  const numScale = spring({ frame: Math.max(0, frame - sToF(numS - s)), fps, config: { damping: 12, stiffness: 85 } });
  const numOp = appear(t, numS, 0.12);
  const lineW = interpolate(t, [titleS - 0.15, titleS + 0.5], [0, 150], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const titleOp = appear(t, titleS, 0.16);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", flexDirection: "column", opacity: op }}>
      <p style={{ color: ACCENT_GOLD, fontSize: 21, fontWeight: 700, letterSpacing: "0.32em", textTransform: "uppercase", margin: 0, marginBottom: 12, textShadow: TS, opacity: appear(t, labelS, 0.2) }}>{labelText}</p>
      <h1 style={{ color: TEXT, fontSize: "9rem", fontWeight: 800, margin: 0, lineHeight: 1, transform: `scale(${numScale})`, textShadow: TS, opacity: numOp }}>{num}</h1>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 20 }}>
        <div style={{ height: 1, width: lineW, backgroundColor: ACCENT_GOLD }} />
        <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: ACCENT_GOLD, opacity: titleOp, boxShadow: `0 0 8px ${ACCENT_GOLD}` }} />
        <div style={{ height: 1, width: lineW, backgroundColor: ACCENT_GOLD }} />
      </div>
      <h2 style={{ color: SECONDARY, fontSize: "2.5rem", fontWeight: 800, letterSpacing: "0.05em", margin: 0, marginTop: 12, textShadow: TS, opacity: titleOp }}>{title}</h2>
      {sub && <p style={{ color: TERTIARY, fontSize: "1.15rem", marginTop: 9, letterSpacing: "0.14em", textShadow: TS, opacity: appear(t, titleS + 0.3, 0.25) }}>{sub}</p>}
    </AbsoluteFill>
  );
};

// ─── 6 · EQUATION STACK — line1 / op / line2 ──────────────────────────
const EquationStack: React.FC<{
  s: number; l1: string; op: string; l2: string; l1S: number; opS: number; l2S: number; outS: number;
}> = ({ s, l1, op, l2, l1S, opS, l2S, outS }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const wOp = win(t, l1S, outS);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: wOp }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <div style={{ color: TEXT, fontSize: 56, fontWeight: 900, letterSpacing: "0.03em", textShadow: TS, opacity: appear(t, l1S, 0.16), transform: `scale(${interpolate(t, [l1S, l1S + 0.25], [0.85, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })})` }}>{l1}</div>
        <div style={{ color: ACCENT_GOLD, fontSize: 46, fontWeight: 900, textShadow: TS, opacity: appear(t, opS, 0.14) }}>{op}</div>
        <div style={{ color: TEXT, fontSize: 56, fontWeight: 900, letterSpacing: "0.03em", textShadow: TS, opacity: appear(t, l2S, 0.16), transform: `scale(${interpolate(t, [l2S, l2S + 0.25], [0.85, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })})` }}>{l2}</div>
      </div>
    </AbsoluteFill>
  );
};

// ─── 7 · END CARD ─────────────────────────────────────────────────────
const EndCard: React.FC<{ s: number; atS: number; outS: number; title?: string; sub?: string }> = ({ s, atS, outS, title = "THAT IS HOW MONEY IS REALLY MADE", sub = "FACTORY FILES · SUBSCRIBE" }) => {
  const frame = useCurrentFrame();
  const t = s + frame / FPS;
  const op = win(t, atS, outS);
  const subOp = appear(t, atS + 0.6, 0.3);
  const nextOp = appear(t, atS + 1.4, 0.3);
  return (
    <AbsoluteFill style={{ fontFamily: FONT, alignItems: "center", justifyContent: "center", opacity: op }}>
      <div style={{ color: ACCENT_GOLD, fontSize: 26, letterSpacing: "0.32em", marginBottom: 22, textShadow: TS, fontWeight: 800 }}>FACTORY FILES</div>
      <div style={{ color: TEXT, fontSize: 52, fontWeight: 900, letterSpacing: "-1px", textShadow: TS, textAlign: "center", lineHeight: 1.12, maxWidth: 1100 }}>{title}</div>
      <div style={{ color: SECONDARY, fontSize: 22, letterSpacing: "0.16em", marginTop: 26, textShadow: TS, opacity: subOp }}>{sub}</div>
      <div style={{ color: TERTIARY, fontSize: 14, letterSpacing: "0.24em", marginTop: 14, textShadow: TS, opacity: nextOp }}>TOP-VOTED COMMENT BECOMES OUR NEXT VIDEO</div>
    </AbsoluteFill>
  );
};

// ════════════════════════════════════════════════════════════════════
// MASTER TIMELINE — every window tied to word_timestamps
// ════════════════════════════════════════════════════════════════════
type Item = { from: number; to: number; node: (s: number) => React.ReactNode };

const ITEMS: Item[] = [
  // Item 0 — Stamp: $100 BILL
  { from: 0.4, to: 6.0, node: (s) => <Stamp s={s} title="$100 BILL" sub="ONE SECRET INSIDE" atS={0.773} outS={6.0} color={PRIMARY} rotate={-6} /> },
  // Item 1 — BigStat: BLUE RIBBON · MICRO-OPTICS
  { from: 7.2, to: 13.5, node: (s) => <BigStat s={s} label="BLUE RIBBON · MICRO-OPTICS" value={1000000} valueS={7.71} decimals={0} suffix=" LENSES / SQ INCH" sub="smaller than a human cell" outS={13.5} /> },
  // Item 2 — TextHighlight: COPY IT
  { from: 13.0, to: 19.6, node: (s) => <TextHighlight s={s} pre="no known technology on earth can" hi="COPY IT" post="· NOT YET" hiS={14.588} hiColor={ACCENT_GOLD} outS={19.6} /> },
  // Item 3 — LowerThird: ITALIAN POLICE · 2024
  { from: 26.0, to: 33.0, node: (s) => <LowerThird s={s} name="ITALIAN POLICE · 2024" sub="LARGEST COUNTERFEIT OPERATION IN EU HISTORY" nameS={26.529} subS={26.829} outS={33.0} /> },
  // Item 4 — BigStat: ONE MAN · WORKING ALONE
  { from: 35.4, to: 41.0, node: (s) => <BigStat s={s} label="ONE MAN · WORKING ALONE" value={250} valueS={35.822} decimals={0} suffix="M EUROS" sub="printed in fake bills · 50M sold" outS={41.0} /> },
  // Item 5 — Stamp: ECB
  { from: 41.0, to: 45.5, node: (s) => <Stamp s={s} title="ECB" sub="FORCED REDESIGN" atS={41.17} outS={45.5} color={PRIMARY} rotate={-4} /> },
  // Item 6 — TextHighlight: ARMS RACE
  { from: 45.4, to: 50.0, node: (s) => <TextHighlight s={s} pre="this is the" hi="ARMS RACE" post="between makers & fakers" hiS={45.799} hiColor={ACCENT_GOLD} outS={50.0} /> },
  // Item 7 — LowerThird: WASHINGTON D.C. · FORT WORTH, TX
  { from: 56.5, to: 63.5, node: (s) => <LowerThird s={s} name="WASHINGTON D.C. · FORT WORTH, TX" sub="BUREAU OF ENGRAVING AND PRINTING" nameS={57.119} subS={57.419} outS={63.5} /> },
  // Item 8 — BigStat: U.S. CURRENCY · DAILY OUTPUT
  { from: 64.0, to: 69.0, node: (s) => <BigStat s={s} label="U.S. CURRENCY · DAILY OUTPUT" value={541} valueS={64.578} decimals={0} suffix="M DOLLARS" sub="newly printed · every single day" outS={69.0} /> },
  // Item 9 — BigStat: BANKNOTES · ANNUAL
  { from: 69.0, to: 73.5, node: (s) => <BigStat s={s} label="BANKNOTES · ANNUAL" value={1.5} valueS={69.349} decimals={1} suffix=" BILLION" sub="produced every year" outS={73.5} /> },
  // Item 10 — BigStat: GLOBAL BANKNOTE INDUSTRY
  { from: 74.0, to: 80.0, node: (s) => <BigStat s={s} label="GLOBAL BANKNOTE INDUSTRY" value={14.9} valueS={74.287} decimals={1} suffix="B USD" sub="150B notes · 180+ currencies" outS={80.0} /> },
  // Item 11 — EquationStack: 75% COTTON + 25% LINEN
  { from: 85.4, to: 90.5, node: (s) => <EquationStack s={s} l1="75% COTTON" op="+" l2="25% LINEN" l1S={85.763} opS={87.085} l2S={87.506} outS={90.5} /> },
  // Item 12 — Stamp: CRANE CURRENCY
  { from: 98.3, to: 104.5, node: (s) => <Stamp s={s} title="CRANE CURRENCY" sub="SOLE U.S. SUPPLIER · SINCE 1964" atS={98.639} outS={104.5} color={PRIMARY} rotate={-7} /> },
  // Item 13 — Chapter 01: SUBSTRATE FORMATION
  { from: 110.2, to: 117.0, node: (s) => <Chapter s={s} num="01" title="SUBSTRATE FORMATION" sub="COTTON-LINEN PULP · WET WEB" numS={110.513} titleS={110.993} outS={117.0} /> },
  // Item 14 — LowerThird: METALLIC SECURITY THREAD
  { from: 131.7, to: 135.5, node: (s) => <LowerThird s={s} name="METALLIC SECURITY THREAD" sub="WOVEN WHILE THE SHEET IS STILL WET" nameS={132.017} subS={132.317} outS={135.5} /> },
  // Item 15 — TextHighlight: BENJAMIN FRANKLIN
  { from: 149.0, to: 155.0, node: (s) => <TextHighlight s={s} pre="watermark · second portrait of" hi="BENJAMIN FRANKLIN" post="visible only against light" hiS={149.456} hiColor={ACCENT_GOLD} outS={155.0} /> },
  // Item 16 — Chapter 02: DESIGN & ENGRAVING
  { from: 168.7, to: 175.5, node: (s) => <Chapter s={s} num="02" title="DESIGN & ENGRAVING" sub="THE LAST MANUAL STEP" numS={168.953} titleS={169.765} outS={175.5} /> },
  // Item 17 — TextHighlight: IRREDUCIBLY MANUAL
  { from: 175.5, to: 180.5, node: (s) => <TextHighlight s={s} pre="remains" hi="IRREDUCIBLY MANUAL" post="drawn by hand · cut under microscope" hiS={175.795} hiColor={ACCENT_GOLD} outS={180.5} /> },
  // Item 18 — BigStat: ONE PORTRAIT · ENGRAVING TIME
  { from: 190.2, to: 195.5, node: (s) => <BigStat s={s} label="ONE PORTRAIT · ENGRAVING TIME" value={100} valueS={190.544} decimals={0} prefix="" suffix="+ HOURS" sub="hand-cut · steel die · under microscope" outS={195.5} /> },
  // Item 19 — Stamp: U.S. PAT. 10,150,283
  { from: 216.4, to: 222.5, node: (s) => <Stamp s={s} title="U.S. PAT. 10,150,283" sub="INTAGLIO · HARDEST TO COUNTERFEIT" atS={216.739} outS={222.5} color={PRIMARY} rotate={-5} /> },
  // Item 20 — Chapter 03: OFFSET PRINTING
  { from: 225.4, to: 232.0, node: (s) => <Chapter s={s} num="03" title="OFFSET PRINTING" sub="BACKGROUND · GUILLOCHE PATTERNS" numS={225.697} titleS={225.998} outS={232.0} /> },
  // Item 21 — BigStat: SECURITY OFFSET PRESS
  { from: 244.4, to: 250.0, node: (s) => <BigStat s={s} label="SECURITY OFFSET PRESS" value={50} valueS={244.679} decimals={0} suffix=" FEET LONG" sub="both sides printed simultaneously" outS={250.0} /> },
  // Item 22 — TextHighlight: $1 BILL
  { from: 258.0, to: 265.0, node: (s) => <TextHighlight s={s} pre="this step does NOT exist on the" hi="$1 BILL" post="it never went through offset" hiS={259.928} hiColor={ACCENT_GOLD} outS={265.0} /> },
  // Item 23 — Chapter 04: INTAGLIO PRINTING
  { from: 278.7, to: 285.0, node: (s) => <Chapter s={s} num="04" title="INTAGLIO PRINTING" sub="THE MAIN EVENT · RAISED TEXTURE" numS={278.837} titleS={279.484} outS={285.0} /> },
  // Item 24 — EquationStack: 70-TON PRESSES → 10,000 SHEETS / HOUR
  { from: 315.9, to: 322.5, node: (s) => <EquationStack s={s} l1="70-TON PRESSES" op="→" l2="10,000 SHEETS / HOUR" l1S={316.202} opS={317.787} l2S={317.787} outS={322.5} /> },
  // Item 25 — BigStat: INTAGLIO INK · DRY TIME
  { from: 323.3, to: 329.0, node: (s) => <BigStat s={s} label="INTAGLIO INK · DRY TIME" value={72} valueS={323.583} decimals={0} suffix=" HOURS" sub="vault-controlled drying rooms" outS={329.0} /> },
  // Item 26 — Chapter 05: SPECIALIZED LAYERS
  { from: 335.1, to: 341.5, node: (s) => <Chapter s={s} num="05" title="SPECIALIZED LAYERS" sub="OVI INK · 3D RIBBON" numS={335.404} titleS={336.012} outS={341.5} /> },
  // Item 27 — TextHighlight: OPTICALLY VARIABLE INK
  { from: 343.8, to: 350.0, node: (s) => <TextHighlight s={s} pre="screen-printed layer of" hi="OPTICALLY VARIABLE INK" post="copper → green when tilted" hiS={344.089} hiColor={ACCENT_GOLD} outS={350.0} /> },
  // Item 28 — BigStat: 3D SECURITY RIBBON
  { from: 383.3, to: 391.0, node: (s) => <BigStat s={s} label="3D SECURITY RIBBON" value={1000000} valueS={383.639} decimals={0} suffix="+ MICRO-LENSES" sub="woven through the paper · not printed on" outS={391.0} /> },
  // Item 29 — Stamp: 10+ YEARS R&D
  { from: 401.7, to: 407.5, node: (s) => <Stamp s={s} title="10+ YEARS R&D" sub="ONE FEATURE · CLASSIFIED PIGMENT" atS={402.125} outS={407.5} color={PRIMARY} rotate={-6} /> },
  // Item 30 — Chapter 06: OPTICAL INSPECTION
  { from: 404.2, to: 411.0, node: (s) => <Chapter s={s} num="06" title="OPTICAL INSPECTION" sub="GOLDEN TEMPLATE · ALGORITHMIC GAUNTLET" numS={404.475} titleS={405.129} outS={411.0} /> },
  // Item 31 — BigStat: INSPECTION LINE · LENGTH
  { from: 441.9, to: 444.6, node: (s) => <BigStat s={s} label="INSPECTION LINE · LENGTH" value={144} valueS={442.396} decimals={0} suffix=" FEET" sub="end-to-end · adaptive cameras" outS={444.6} /> },
  // Item 32 — Chapter 07: LETTERPRESS & SEALS
  { from: 444.8, to: 451.5, node: (s) => <Chapter s={s} num="07" title="LETTERPRESS & SEALS" sub="SERIAL NUMBER · TREASURY · FED SEAL" numS={445.192} titleS={445.993} outS={451.5} /> },
  // Item 33 — Chapter 08: CUTTING & PACKAGING
  { from: 476.4, to: 483.0, node: (s) => <Chapter s={s} num="08" title="CUTTING & PACKAGING" sub="STRAPS · BRICKS · ARMORED SKIDS" numS={476.734} titleS={477.362} outS={483.0} /> },
  // Item 34 — BigStat: ONE SHRINK-WRAPPED PACKAGE
  { from: 492.0, to: 498.5, node: (s) => <BigStat s={s} label="ONE SHRINK-WRAPPED PACKAGE" value={4000} valueS={492.318} decimals={0} suffix=" NOTES" sub="loaded onto armored trucks" outS={498.5} /> },
  // Item 35 — EquationStack: $1 BILL · 3.2¢ · $100 BILL · 9.4¢
  { from: 515.7, to: 525.0, node: (s) => <EquationStack s={s} l1="$1 BILL · 3.2¢" op="·" l2="$100 BILL · 9.4¢" l1S={516.375} opS={520.953} l2S={520.953} outS={525.0} /> },
  // Item 36 — Stamp: SEIGNIORAGE
  { from: 542.4, to: 550.0, node: (s) => <Stamp s={s} title="SEIGNIORAGE" sub="9.4¢ COST · $100 FACE VALUE" atS={542.932} outS={550.0} color={PRIMARY} rotate={-5} /> },
  // Item 37 — BigStat: 2025 PRINT ORDER · FACE VALUE
  { from: 551.0, to: 558.5, node: (s) => <BigStat s={s} label="2025 PRINT ORDER · FACE VALUE" value={113} valueS={551.367} decimals={0} prefix="$" suffix="B (UP TO)" sub="from $83.2B · cost = fraction of total" outS={558.5} /> },
  // Item 38 — TextHighlight: MOST PROFITABLE MANUFACTURER
  { from: 561.4, to: 567.5, node: (s) => <TextHighlight s={s} pre="the U.S. government is the" hi="MOST PROFITABLE MANUFACTURER" post="on earth" hiS={561.986} hiColor={ACCENT_GOLD} outS={567.5} /> },
  // Item 39 — BigStat: ONE PENNY · COST TO MINT
  { from: 567.4, to: 574.5, node: (s) => <BigStat s={s} label="ONE PENNY · COST TO MINT" value={3.69} valueS={567.677} decimals={2} suffix="¢" sub="face value = 1¢ · loses money every coin" outS={574.5} /> },
  // Item 40 — LowerThird: PENNY · 1793 → 2025
  { from: 581.4, to: 588.0, node: (s) => <LowerThird s={s} name="PENNY · 1793 → 2025" sub="OFFICIALLY DEAD · UK + AU DID IT YEARS AGO" nameS={581.689} subS={581.989} outS={588.0} /> },
  // Item 41 — BigStat: PUBLIC RESPONSE · COMMENTS
  { from: 608.4, to: 614.5, node: (s) => <BigStat s={s} label="PUBLIC RESPONSE · COMMENTS" value={502} valueS={608.627} decimals={0} suffix=" PEOPLE" sub="most do not believe the lone-wolf story" outS={614.5} /> },
  // Item 42 — TextHighlight: ARMS RACE NEVER ENDS
  { from: 641.0, to: 647.5, node: (s) => <TextHighlight s={s} pre="the" hi="ARMS RACE NEVER ENDS" post="" hiS={641.416} hiColor={ACCENT_GOLD} outS={647.5} /> },
  // Item 43 — EndCard
  { from: 657.3, to: 664.5, node: (s) => <EndCard s={s} atS={657.545} outS={664.5} title="THAT IS HOW MONEY IS REALLY MADE" sub="FACTORY FILES · SUBSCRIBE" /> },
];

export const MoneyFullPreview: React.FC = () => {
  return (
    <AbsoluteFill>
      {ITEMS.map((it, i) => (
        <Sequence key={i} from={sToF(it.from)} durationInFrames={Math.max(1, sToF(it.to) - sToF(it.from))} layout="none">
          {it.node(it.from)}
        </Sequence>
      ))}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
